import { AxiosError } from 'axios'
import { isEqual } from 'lodash'
import React, { useState, useEffect, useCallback } from 'react'

import useAuth from 'hooks/use-auth'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import {
  ServiceGoldenSignal as ServiceGoldenServiceType,
  TeamExternalIntegration
} from 'types/api/services/golden-signal'
import { StatusCheck } from 'types/api/services/status'
import {
  completeTeamExternalIntegrations,
  convertResponseToTeamExternalIntegration,
  convertStatusCheckToGoldenSignal,
  removeSavedTeamExternalIntegrations
} from 'utils/team-external-integrations/team-external-integrations-util'

type IntegrationResponse = {
  integrations: TeamExternalIntegration[]
}

export interface EditGoldenSignalContextValues {
  integration: TeamExternalIntegration
  setIntegration: (i: TeamExternalIntegration) => void
  // /status-checks Axios methods
  goldenSignals: ServiceGoldenServiceType[]
  reFetchGoldenSignals: () => void
  goldenSignalFetchError: AxiosError<unknown, any>
  isGoldenSignalFetchLoading: boolean
  hasGoldenSignalFetchError: boolean
  // /external-integrations Axios methods
  externalIntegrations: IntegrationResponse
  externalIntegrationsError: AxiosError<unknown, any>
  hasExternalIntegrationsError: boolean
  isExternalIntegrationsLoading: boolean
  reFetchExternalIntegrations: () => void
  getAvailableExternalIntegrations: (
    removeSavedIntegrations: boolean
  ) => TeamExternalIntegration[]
  externalIntegrationType: 'GOLDEN_SIGNAL' | 'LOAD_GENERATOR'
}

export const DEFAULT_EDIT_GS_VALUES: EditGoldenSignalContextValues = {
  integration: null,
  setIntegration: () => {},
  goldenSignals: [],
  reFetchGoldenSignals: () => false,
  goldenSignalFetchError: null,
  isGoldenSignalFetchLoading: false,
  hasGoldenSignalFetchError: false,
  externalIntegrations: { integrations: [] },
  externalIntegrationsError: null,
  hasExternalIntegrationsError: false,
  isExternalIntegrationsLoading: false,
  reFetchExternalIntegrations: () => false,
  getAvailableExternalIntegrations: () => [],
  externalIntegrationType: 'GOLDEN_SIGNAL'
}
export const EditGoldenSignalContext =
  React.createContext<EditGoldenSignalContextValues>(DEFAULT_EDIT_GS_VALUES)

export const EditGoldenSignalProvider: React.FC<{
  statusChecks?: StatusCheck[]
  statusCheckIds?: string[]
  externalIntegrationType: 'GOLDEN_SIGNAL' | 'LOAD_GENERATOR'
  children
}> = (props) => {
  const { currentTeamId } = useAuth()
  const [integration, setIntegration] = useState<TeamExternalIntegration>(null)
  const {
    statusCheckIds = [],
    children,
    externalIntegrationType,
    statusChecks: initialStatusChecks = []
  } = props
  const [statusChecks, setStatusChecks] =
    useState<StatusCheck[]>(initialStatusChecks)

  /**
   * requesting the existing golden signal items for the service
   */
  const {
    data: returnedGoldenSignalData,
    error: goldenSignalFetchError,
    isFetching: isGoldenSignalFetchLoading,
    isError: hasGoldenSignalFetchError,
    refetch: reFetchGoldenSignals
  } = useGremlinQuery<StatusCheck[]>(
    `status-checks?statusCheckIds=${statusCheckIds.join('&statusCheckIds=')}`,
    {},
    {
      enabled: statusCheckIds?.length > 0,
      placeholderData: null
    }
  )

  const {
    error: externalIntegrationsError,
    data: externalIntegrations,
    isFetching: isExternalIntegrationsLoading,
    isError: hasExternalIntegrationsError,
    refetch: reFetchExternalIntegrations
  } = useGremlinQuery<IntegrationResponse>(
    `external-integrations/${
      externalIntegrationType === 'GOLDEN_SIGNAL'
        ? 'status-check'
        : 'load-generator'
    }?teamId=${currentTeamId}`,
    {},
    {}
  )

  const savedTeamExternalIntegrations =
    convertResponseToTeamExternalIntegration(externalIntegrations)

  const getAvailableExternalIntegrations = useCallback(
    (removeSavedIntegrations) => {
      return removeSavedIntegrations
        ? removeSavedTeamExternalIntegrations(
            completeTeamExternalIntegrations(externalIntegrationType, [], true),
            savedTeamExternalIntegrations
          )
        : completeTeamExternalIntegrations(
            externalIntegrationType,
            savedTeamExternalIntegrations,
            true
          )
    },
    [externalIntegrationType, savedTeamExternalIntegrations]
  )

  /**
   * In the event that the user updates the currently selected Gold Signal's integration, that update
   * results in a re-pull of the external integrations API, this proceeds to update the currently
   * selected integration with the updated collection.
   */
  useEffect(() => {
    if (integration) {
      const currentIntegration = getAvailableExternalIntegrations(false).find(
        (inte) => inte.name === integration.name
      )
      if (!isEqual(currentIntegration, integration)) {
        setIntegration(currentIntegration)
      }
    }
  }, [externalIntegrations, integration, getAvailableExternalIntegrations])

  /**
   * When an already fetched statusChecks collection is passed through the props to the context, and no
   * statusCheckIds are supplied, it will already have the collection applied to the local statusChecks
   * state. When the status-checks runs, that collection is applied to the local statusChecks state.
   *
   */
  useEffect(() => {
    if (returnedGoldenSignalData?.length > 0) {
      setStatusChecks(returnedGoldenSignalData)
    }
  }, [returnedGoldenSignalData])

  const goldenSignals =
    statusChecks && !hasGoldenSignalFetchError
      ? statusChecks.map((s) => convertStatusCheckToGoldenSignal(s))
      : []

  return (
    <EditGoldenSignalContext.Provider
      value={{
        integration,
        setIntegration,
        goldenSignals,
        reFetchGoldenSignals,
        goldenSignalFetchError,
        isGoldenSignalFetchLoading,
        hasGoldenSignalFetchError,
        externalIntegrations,
        externalIntegrationsError,
        hasExternalIntegrationsError,
        isExternalIntegrationsLoading,
        reFetchExternalIntegrations,
        getAvailableExternalIntegrations,
        externalIntegrationType
      }}
    >
      {children}
    </EditGoldenSignalContext.Provider>
  )
}
